/**
 * @module
 *
 * @import {ImageFormat} from 'acng/ts/core/image';
 */

// bounty, stargazer, relativity, rubicon
import {attributeSelector} from '@acng/frontend-bounty/dom/query.js';
import {SRC, unset} from '@acng/frontend-bounty/dom/attribute.js';
import {AttributeLink, AttributeSetup} from '@acng/frontend-stargazer';
import {Watch} from '@acng/frontend-relativity';
import {IS, guard} from '@acng/frontend-rubicon';
// enterprise
import {getFallbackImageUrl} from '../../layout/service/fallback-image.js';
import {IMAGE_FORMAT} from 'acng/model';
import {ctxAmateur} from '../context/amateur.js';

/**
 * Set the image source to the preferred profile image of the
 * {@link ctxAmateur | local amateur}.
 *
 * The {@link ImageFormat} is required.
 *
 * ## Formal Syntax
 *
 *     amateur-image-src = <image-format>
 *     <image-format> = {@link ImageFormat}
 *
 * @example
 *
 * ```html
 * <img amateur-image-src="medium" />
 * ```
 *
 * @group DOM Attribute
 */
const name = 'amateur-image-src';

AttributeSetup(name, (name) => {
  const watch = Watch(attributeSelector(name), ctxAmateur);

  AttributeLink(name, (image, format) =>
    watch(image, (image, amateur) => {
      ASSERT: {
        guard(image, IS_IMAGE, `[${name}]`);
        guard(format, IMAGE_FORMAT, `[${name}="${format}"]`);
      }
      unset(image, SRC);
      image[SRC] = amateur ? amateur.getImageUrl(format) : getFallbackImageUrl('none', format);
      image.onerror = () => {
        const fallbackUrl = getFallbackImageUrl('none', format);
        if (image[SRC] !== fallbackUrl) {
          image[SRC] = fallbackUrl;
        }
      }
    }),
  );
});

const IS_IMAGE = /* @__PURE__ */ IS(HTMLImageElement);

export {name as AMATEUR_IMAGE_SRC};
